import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import CallToAction from "../components/CallToAction";
import { 
  FacebookIcon, 
  TwitterIcon, 
  LinkedinIcon, 
  ChevronLeft,
  Clock, 
  Calendar,
  User
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Mock blog posts data
const blogPosts = [
  {
    id: 1,
    title: "Vse, kar morate vedeti o korekciji vek",
    slug: "vse-o-korekciji-vek",
    excerpt: "Korekcija vek je eden najbolj priljubljenih estetskih posegov, ki lahko dramatično pomladi vaš videz.",
    content: `
      <p class="mb-4">Blefaroplastika ali korekcija vek je kirurški poseg, ki odstranjuje odvečno kožo, mišice in/ali maščobo z zgornjih ali spodnjih vek. Z leti koža okoli oči postane ohlapnejša, kar povzroči "težke" zgornje veke in vrečke pod očmi.</p>
      
      <h2 class="text-2xl font-bold mt-8 mb-4">Kdaj je korekcija vek primerna?</h2>
      <p class="mb-4">Korekcija vek je primerna za ljudi, ki imajo:</p>
      <ul class="list-disc pl-6 mb-4">
        <li class="mb-2">Povešene ali ohlapne zgornje veke, ki včasih vplivajo na periferni vid</li>
        <li class="mb-2">Odvečno kožo na zgornjih vekah, ki ustvarja gube</li>
        <li class="mb-2">Vrečke pod očmi</li>
        <li class="mb-2">Odvečno kožo na spodnjih vekah</li>
      </ul>
      
      <h2 class="text-2xl font-bold mt-8 mb-4">Potek posega</h2>
      <p class="mb-4">Korekcija vek običajno poteka v lokalni anesteziji s sedacijo ali v splošni anesteziji, odvisno od obsega posega in preferencah pacienta. Postopek običajno traja 1-2 uri.</p>
      <p class="mb-4">Pri korekciji zgornjih vek kirurg naredi rez v naravni gubi veke, odstrani odvečno kožo, mišico in maščobo ter nato zašije rez. Pri korekciji spodnjih vek se rez običajno naredi tik pod linijo trepalnic ali znotraj spodnje veke.</p>
      
      <h2 class="text-2xl font-bold mt-8 mb-4">Okrevanje po posegu</h2>
      <p class="mb-4">Po korekciji vek lahko pričakujete:</p>
      <ul class="list-disc pl-6 mb-4">
        <li class="mb-2">Modrice in otekline, ki običajno trajajo 1-2 tedna</li>
        <li class="mb-2">Suhe oči, ki jih lajšamo s kapljicami za oči</li>
        <li class="mb-2">Občutljivost na svetlobo, ki se sčasoma zmanjša</li>
        <li class="mb-2">Rahlo zamegljenost vida, ki je začasna</li>
      </ul>
      <p class="mb-4">Večina pacientov se lahko vrne k običajnim aktivnostim po 7-10 dneh, vendar se je priporočljivo izogibati napornemu fizičnemu delu in športnim aktivnostim za vsaj 3-4 tedne.</p>
      
      <h2 class="text-2xl font-bold mt-8 mb-4">Rezultati</h2>
      <p class="mb-4">Rezultati korekcije vek so običajno dolgotrajni. Zgornje veke lahko ostanejo izboljšane za 5-7 let ali celo dlje. Spodnje veke redko potrebujejo ponoven poseg. Seveda je proces staranja neizogiben, zato se lahko sčasoma pojavi nova ohlapnost, vendar nikoli ne bo tako izrazita kot pred posegom.</p>
      
      <h2 class="text-2xl font-bold mt-8 mb-4">Možni zapleti</h2>
      <p class="mb-4">Kot pri vsakem kirurškem posegu, obstajajo tudi pri korekciji vek določena tveganja, vključno z:</p>
      <ul class="list-disc pl-6 mb-4">
        <li class="mb-2">Krvavitvijo in modricami</li>
        <li class="mb-2">Infekcijo</li>
        <li class="mb-2">Težavami s celjenjem ran</li>
        <li class="mb-2">Začasno ali trajno spremembo občutljivosti</li>
        <li class="mb-2">Asimetrijo</li>
        <li class="mb-2">V redkih primerih, težavami z zapiranjem oči</li>
      </ul>
      <p class="mb-4">Večina teh zapletov je redkih in jih je mogoče učinkovito obvladati z ustrezno nego po posegu.</p>
      
      <h2 class="text-2xl font-bold mt-8 mb-4">Zaključek</h2>
      <p class="mb-4">Korekcija vek je učinkovit način za pomladitev področja okoli oči in lahko dramatično izboljša vaš videz ter v nekaterih primerih tudi vid. Kot pri vseh estetskih posegih je pomembno, da imate realna pričakovanja in da se o posegu temeljito pogovorite s svojim kirurgom.</p>
      <p class="mb-4">Za več informacij ali za rezervacijo posveta se obrnite na našo kliniko. Z veseljem vam bomo odgovorili na vsa vprašanja in vam pomagali pri odločitvi, ali je korekcija vek prava izbira za vas.</p>
    `,
    imageUrl: "https://images.unsplash.com/photo-1571942794135-56e61c8ac80e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
    category: "face",
    date: "2024-04-15",
    author: "Dr. Danijel Novak",
    authorImage: "https://images.unsplash.com/photo-1622253692010-333f2da6031d?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80",
    authorBio: "Dr. Danijel Novak je specialist plastične, rekonstruktivne in estetske kirurgije z več kot 15 let izkušenj.",
    readTime: "5 min",
    tags: ["korekcija vek", "blefaroplastika", "estetska kirurgija", "pomladitev"]
  },
  {
    id: 2,
    title: "Kako pripraviti kožo na poletje",
    slug: "priprava-koze-na-poletje",
    excerpt: "Z bližajočim se poletjem je pomembno, da svojo kožo primerno pripravite na sonce in visoke temperature.",
    content: `
      <p class="mb-4">Z bližajočim se poletjem je pomembno, da svojo kožo primerno pripravite na sonce in visoke temperature. Primerna priprava lahko pomaga preprečiti poškodbe kože zaradi sonca, prezgodnje staranje in zmanjša tveganje za kožnega raka.</p>
      
      <h2 class="text-2xl font-bold mt-8 mb-4">1. Luščenje odmrlih celic kože</h2>
      <p class="mb-4">Eden prvih korakov pri pripravi kože na poletje je nežno luščenje ali eksfoliacija. Ta proces odstrani odmrle celice kože, ki se nabirajo na površini, in omogoča koži, da diha. Rezultat je bolj gladka in sijoča koža.</p>
      <p class="mb-4">Priporočamo uporabo nežnih piling izdelkov, ki vsebujejo alfa hidroksi kisline (AHA) ali beta hidroksi kisline (BHA). Te kisline pomagajo razgraditi odmrle celice kože brez draženja. Izogibajte se grobim pilingom z abrazivnimi delci, ki lahko poškodujejo kožo.</p>
      
      <h2 class="text-2xl font-bold mt-8 mb-4">2. Hidratacija od znotraj in zunaj</h2>
      <p class="mb-4">Hidratacija je ključnega pomena za zdravo in odporno kožo. Poleti, ko so temperature višje, koža hitreje izgublja vlago, zato je pomembno, da jo nadomestite.</p>
      <ul class="list-disc pl-6 mb-4">
        <li class="mb-2">Pijte dovolj vode (vsaj 8 kozarcev na dan)</li>
        <li class="mb-2">Uporabljajte lahko vlažilno kremo, ki vsebuje hialuronsko kislino ali glicerin</li>
        <li class="mb-2">Izogibajte se alkoholu in kofeinu, ki lahko dehidrirata kožo</li>
        <li class="mb-2">Uživajte hrano, bogato z vodo, kot so kumare, lubenica in zelena</li>
      </ul>
      
      <h2 class="text-2xl font-bold mt-8 mb-4">3. Zaščita pred soncem</h2>
      <p class="mb-4">Zaščita pred soncem je najpomembnejši korak pri pripravi kože na poletje. Ultravijolični (UV) žarki lahko poškodujejo kožne celice, povzročijo prezgodnje staranje in povečajo tveganje za kožnega raka.</p>
      <p class="mb-4">Nekaj ključnih nasvetov za zaščito pred soncem:</p>
      <ul class="list-disc pl-6 mb-4">
        <li class="mb-2">Uporabljajte širokospektralno sončno kremo s SPF 30 ali več</li>
        <li class="mb-2">Nanesite sončno kremo 20-30 minut pred izpostavljanjem soncu</li>
        <li class="mb-2">Ponovno nanesite sončno kremo vsaki dve uri ali po plavanju ali potenju</li>
        <li class="mb-2">Nosite zaščitna oblačila, klobuk in sončna očala</li>
        <li class="mb-2">Izogibajte se soncu med 10. in 16. uro, ko so sončni žarki najmočnejši</li>
      </ul>
      
      <h2 class="text-2xl font-bold mt-8 mb-4">4. Antioksidanti za zaščito celic</h2>
      <p class="mb-4">Antioksidanti pomagajo zaščititi kožo pred prostimi radikali, ki jih povzročajo UV žarki in onesnaževanje. Prosti radikali lahko poškodujejo celice kože in pospešijo proces staranja.</p>
      <p class="mb-4">Najboljši antioksidanti za kožo vključujejo:</p>
      <ul class="list-disc pl-6 mb-4">
        <li class="mb-2">Vitamin C: Svetli kožo in stimulira proizvodnjo kolagena</li>
        <li class="mb-2">Vitamin E: Ohranja vlažnost kože in jo ščiti pred poškodbami</li>
        <li class="mb-2">Resveratrol: Pomaga pri zaščiti pred UV poškodbami</li>
        <li class="mb-2">Niacinamid (vitamin B3): Zmanjšuje rdečico in izboljšuje teksturo kože</li>
      </ul>
      <p class="mb-4">Te antioksidante lahko dobite v obliki serumov ali krem za lokalno uporabo, ali pa jih vključite v svojo prehrano z uživanjem sadja, zelenjave in oreščkov.</p>
      
      <h2 class="text-2xl font-bold mt-8 mb-4">5. Prehrana za zdravo kožo</h2>
      <p class="mb-4">Kar jeste, se odraža na vaši koži. Uravnotežena prehrana, bogata z vitamini, minerali in antioksidanti, lahko pomaga izboljšati zdravje kože od znotraj.</p>
      <p class="mb-4">Živila, ki jih priporočamo za zdravo poletno kožo:</p>
      <ul class="list-disc pl-6 mb-4">
        <li class="mb-2">Maščobne ribe (losos, skuša, sardine): bogate z omega-3 maščobnimi kislinami, ki pomagajo ohranjati kožo navlaženo</li>
        <li class="mb-2">Agrumi: bogati z vitaminom C, ki je bistven za proizvodnjo kolagena</li>
        <li class="mb-2">Oranžno sadje in zelenjava: vsebujejo beta karoten, ki se v telesu pretvori v vitamin A, pomemben za zdravje kože</li>
        <li class="mb-2">Oreščki in semena: bogati z vitaminom E in cinkom, ki pomagata pri zaščiti celic kože</li>
        <li class="mb-2">Zelena listnata zelenjava: vsebuje antioksidante, ki pomagajo zaščititi kožo pred poškodbami</li>
      </ul>
      
      <h2 class="text-2xl font-bold mt-8 mb-4">Zaključek</h2>
      <p class="mb-4">Priprava kože na poletje zahteva celovit pristop, ki vključuje ustrezno nego kože, zaščito pred soncem, hidratacijo in zdravo prehrano. Z upoštevanjem teh nasvetov bo vaša koža pripravljena na poletne mesece in bo ostala zdrava, sijoča in mladostna.</p>
      <p class="mb-4">Za personalizirano svetovanje glede nege vaše kože se lahko vedno obrnete na naše strokovnjake, ki vam bodo pomagali izbrati najbolj primerne izdelke in postopke za vaš tip kože.</p>
    `,
    imageUrl: "https://images.unsplash.com/photo-1555820585-c5ae44394b79?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
    category: "skincare",
    date: "2024-04-10",
    author: "Maja Kovač",
    authorImage: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80",
    authorBio: "Maja Kovač je specializirana medicinska sestra za nego po estetskih posegih z več kot 10 leti izkušenj.",
    readTime: "4 min",
    tags: ["nega kože", "zaščita pred soncem", "hidratacija", "antioksidanti"]
  }
];

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<(typeof blogPosts)[0] | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<typeof blogPosts>([]);
  
  // Find current post and related posts based on slug
  useEffect(() => {
    const currentPost = blogPosts.find(post => post.slug === slug);
    
    if (currentPost) {
      setPost(currentPost);
      
      // Find related posts with the same category
      const related = blogPosts
        .filter(p => p.id !== currentPost.id && p.category === currentPost.category)
        .slice(0, 3);
      
      setRelatedPosts(related);
    }
  }, [slug]);

  // Format date function
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sl-SI', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (!post) {
    return (
      <Layout>
        <div className="container-section text-center">
          <h2 className="text-2xl font-bold mb-4">Članek ni bil najden</h2>
          <p className="text-gray-700">Žal članek, ki ga iščete, ne obstaja.</p>
          <Button asChild className="mt-4 bg-medical-500 hover:bg-medical-600">
            <Link to="/nasveti">Nazaj na blog</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section with Image */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-transparent z-10"></div>
        <img 
          src={post.imageUrl}
          alt={post.title}
          className="w-full h-80 md:h-96 object-cover"
        />
        <div className="absolute inset-0 z-20 flex items-center">
          <div className="container mx-auto px-6 md:px-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {post.title}
            </h1>
          </div>
        </div>
      </div>
      
      {/* Article Content */}
      <article className="bg-white py-12 md:py-16">
        <div className="container mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-8">
              {/* Article Meta */}
              <div className="flex flex-wrap gap-4 mb-8 text-gray-600 text-sm">
                <div className="flex items-center">
                  <Calendar size={16} className="mr-1" />
                  <span>{formatDate(post.date)}</span>
                </div>
                <div className="flex items-center">
                  <Clock size={16} className="mr-1" />
                  <span>{post.readTime} branja</span>
                </div>
                <div className="flex items-center">
                  <User size={16} className="mr-1" />
                  <span>{post.author}</span>
                </div>
              </div>
              
              {/* Article Content */}
              <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: post.content }} />
              
              {/* Tags */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Share */}
              <div className="mt-8">
                <p className="font-medium text-gray-700 mb-3">Deli ta članek:</p>
                <div className="flex gap-3">
                  <button className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700">
                    <FacebookIcon size={18} />
                  </button>
                  <button className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700">
                    <TwitterIcon size={18} />
                  </button>
                  <button className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700">
                    <LinkedinIcon size={18} />
                  </button>
                </div>
              </div>
              
              {/* Author Bio */}
              <div className="mt-12 glass-card p-6 md:p-8">
                <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
                  <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
                    <img 
                      src={post.authorImage}
                      alt={post.author}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{post.author}</h3>
                    <p className="text-gray-700">{post.authorBio}</p>
                  </div>
                </div>
              </div>
              
              {/* Back to Blog */}
              <div className="mt-12">
                <Button asChild variant="outline" className="border-medical-300 text-medical-700">
                  <Link to="/nasveti">
                    <ChevronLeft size={16} className="mr-1" />
                    Nazaj na blog
                  </Link>
                </Button>
              </div>
            </div>
            
            {/* Sidebar */}
            <aside className="lg:col-span-4">
              {/* Related Posts */}
              <div className="glass-card p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Sorodni članki</h3>
                <div className="space-y-6">
                  {relatedPosts.length > 0 ? (
                    relatedPosts.map(relatedPost => (
                      <Link 
                        key={relatedPost.id} 
                        to={`/nasveti/${relatedPost.slug}`}
                        className="block"
                      >
                        <div className="flex gap-4">
                          <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                            <img 
                              src={relatedPost.imageUrl}
                              alt={relatedPost.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 line-clamp-2 hover:text-medical-600">
                              {relatedPost.title}
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">
                              {formatDate(relatedPost.date)}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <p className="text-gray-600">Ni sorodnih člankov.</p>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>
      
      <CallToAction />
    </Layout>
  );
};

export default BlogPostPage;